import { useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";

export default function LandingTerminsvertretung() {
  const handleContactSubmit = useCallback((e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const subject = encodeURIComponent("Verfügbarkeitsanfrage – Terminsvertretung");

    const body = encodeURIComponent(
      `Kanzlei: ${data.get("kanzlei")}
Ansprechpartner: ${data.get("name")}
E-Mail: ${data.get("email")}
Telefon: ${data.get("phone")}

Gericht: ${data.get("gericht")}
Datum: ${data.get("datum")}
Uhrzeit: ${data.get("uhrzeit")}
Aktenzeichen: ${data.get("az")}

Weisungen / Verhandlungskorridor:
${data.get("nachricht")}

Hinweis: Vergütungsvereinbarung gemäß § 3a RVG. Beauftragung ausschließlich zwischen Kanzleien (B2B).`
    );

    window.location.href = `mailto:info@kanzlei-preisner.de?subject=${subject}&body=${body}`;
  }, []);

  const usps = useMemo(
    () => [
      {
        title: "Gründliche Vorbereitung",
        text: "Akte, Schriftsätze und Sachverhalt werden vorab strukturiert ausgewertet.",
      },
      {
        title: "Kurzfristige Übernahme",
        text: "Flexible Verfügbarkeit für Termine vor dem Arbeitsgericht Berlin sowie Videoverhandlungen (bundesweit).",
      },
      {
        title: "Präzise Verhandlungsführung",
        text:
          "Ich verhandle innerhalb des von Ihnen vorgegebenen Vergleichs- bzw. Verhandlungsrahmens – klar strukturiert, präzise und zielorientiert.",
      },
      {
        title: "Terminsbericht am selben Tag",
        text: "Schriftlicher Bericht unmittelbar nach dem Termin.",
      },
      {
        title: "Zeit- & Ressourcenvorteil",
        text:
          "Sie sparen Anreise-, Warte- und Terminzeit. Kein interner Aufwand – Sie können sich auf das Kerngeschäft konzentrieren.",
      },
      {
        title: "Vertrauter Ansprechpartner",
        text:
          "Auf Wunsch langfristige Zusammenarbeit. Wiederkehrende Termine mit derselben Qualität – konsistent, verlässlich, mandantenorientiert.",
      },
    ],
    []
  );

  return (
    <>
      <Helmet>
        <title>Terminsvertretung Arbeitsrecht – RA Thomas M. Preisner</title>
        <meta
          name="description"
          content="Terminsvertretung im Arbeitsrecht für Kanzleien. Zuverlässig, effizient, kurzfristig verfügbar."
        />
      </Helmet>

      <div className="min-h-screen bg-slate-100 text-slate-900">
        {/* HERO */}
        <section className="mx-auto max-w-6xl px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-widest text-slate-600">
              Arbeitsrecht · Berlin
            </p>

            <h1 className="mt-4 text-4xl font-extrabold leading-tight">
              Terminsvertretungen für Kanzleien
              <span className="block">im Arbeitsrecht</span>
              <span className="block text-slate-600 text-2xl mt-2">
                zuverlässig · effizient · kurzfristig
              </span>
            </h1>

            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {usps.map((u, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-300 bg-white p-5 shadow-sm"
                >
                  <div className="font-semibold">{u.title}</div>
                  <p className="mt-1 text-sm text-slate-600">{u.text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ÜBER MICH */}
        <section className="mx-auto max-w-6xl px-6 pb-20">
          <div className="grid gap-10 md:grid-cols-2 items-center">
            <div>
              <h2 className="text-xl font-semibold mb-4">Über mich</h2>
              <ul className="space-y-3 text-slate-700">
                <li>✓ Zugelassen und tätig als Rechtsanwalt in Berlin seit 2013</li>
                <li>
                  ✓ Schwerpunkt Arbeitsrecht & Terminsvertretungen
                  <div className="text-sm text-slate-500 ml-4">
                    (insbesondere Kündigungsschutzverfahren)
                  </div>
                </li>
                <li>✓ Fachanwaltslehrgang Arbeitsrecht (2023)</li>
                <li>✓ Mediatorenausbildung (2025)</li>
                <li>✓ Syndikustätigkeit 2017–2020</li>
              </ul>
            </div>

            <div className="mx-auto h-56 w-56 overflow-hidden rounded-full border shadow">
              <img
                src={`${process.env.PUBLIC_URL}/images/profilfoto-preisner.jpg`}
                alt="Rechtsanwalt Thomas M. Preisner"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* HONORAR */}
        <section className="mx-auto max-w-6xl px-6 py-16">
          <h2 className="text-2xl font-semibold mb-8">
            Honorar & Konditionen (Pauschalen, netto)
          </h2>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl bg-white p-6 shadow">
              <div className="font-semibold">Gütetermin</div>
              <div className="mt-2 text-2xl font-bold">250 €</div>
            </div>
            <div className="rounded-2xl bg-white p-6 shadow">
              <div className="font-semibold">Kammertermin (ohne Vergleich)</div>
              <div className="mt-2 text-2xl font-bold">350 €</div>
            </div>
            <div className="rounded-2xl bg-white p-6 shadow">
              <div className="font-semibold">Kammertermin inkl. Vergleich</div>
              <div className="mt-2 text-2xl font-bold">400 €</div>
            </div>
          </div>
        </section>

        {/* KONTAKT */}
        <section id="kontakt" className="border-t border-neutral-900 bg-slate-100/60">
          <div className="mx-auto max-w-6xl px-6 py-12">
            <h2 className="text-2xl font-semibold">Kontaktformular</h2>
            <p className="mt-1 text-sm text-slate-500">
              Unverbindliche Verfügbarkeitsanfrage.
            </p>

            <form onSubmit={handleContactSubmit} className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="block text-sm">Kanzlei*</label>
                <input name="kanzlei" required className="mt-1 w-full rounded-xl border px-3 py-2.5" />
              </div>

              <div>
                <label className="block text-sm">Ansprechpartner*</label>
                <input name="name" required className="mt-1 w-full rounded-xl border px-3 py-2.5" />
              </div>

              <div>
                <label className="block text-sm">E-Mail*</label>
                <input type="email" name="email" required className="mt-1 w-full rounded-xl border px-3 py-2.5" />
              </div>

              <div>
                <label className="block text-sm">Telefon (optional)</label>
                <input name="phone" className="mt-1 w-full rounded-xl border px-3 py-2.5" />
              </div>

              <div>
                <label className="block text-sm">Gericht*</label>
                <input name="gericht" required className="mt-1 w-full rounded-xl border px-3 py-2.5" />
              </div>

              <div>
                <label className="block text-sm">Datum*</label>
                <input type="date" name="datum" required className="mt-1 w-full rounded-xl border px-3 py-2.5" />
              </div>

              <div>
                <label className="block text-sm">Uhrzeit*</label>
                <input type="time" name="uhrzeit" required className="mt-1 w-full rounded-xl border px-3 py-2.5" />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm">Aktenzeichen*</label>
                <input name="az" required className="mt-1 w-full rounded-xl border px-3 py-2.5" />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm">Nachricht / Weisungen (optional)</label>
                <textarea name="nachricht" rows={5} className="mt-1 w-full rounded-xl border px-3 py-2.5" />
              </div>

              <div className="sm:col-span-2">
                <button className="rounded-2xl bg-sky-500 px-6 py-3 text-white hover:bg-sky-600">
                  Unverbindlich anfragen
                </button>
              </div>
            </form>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="border-t border-neutral-900">
          <div className="mx-auto max-w-6xl px-6 py-10 text-sm text-slate-500">
            <div className="flex gap-6">
              <span>© 2026 Rechtsanwalt Thomas M. Preisner</span>
              <a href={`${process.env.PUBLIC_URL}/impressum`}>Impressum</a>
              <a href={`${process.env.PUBLIC_URL}/datenschutz`}>Datenschutzerklärung</a>
            </div>
            <p className="mt-3 text-xs">
              Terminsvertretung ausschließlich für Anwaltskanzleien.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}


